import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FirebaseAppCheck } from '@capacitor-firebase/app-check';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAppCheckService {
  private initPromise: Promise<void> | null = null;
  private isSuspended = false;

  constructor() {}

  /**
   * Initializes Firebase App Check.
   * Can be called multiple times, but will only execute once.
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      // Direct plugin call to initialize
      if (Capacitor.isNativePlatform()) {
        console.log('Initializing Firebase App Check for Native platform...');
        try {
          // NOTE: debug: true requires registering your device's debug token in the Firebase Console.
          // Look for "AppCheck debug token" in your Logcat/Console logs.
          await FirebaseAppCheck.initialize({ debug: true });
          console.log('Firebase App Check initialized successfully for Native.');
        } catch (error) {
          console.error(
            'Failed to initialize Firebase App Check for Native:',
            error,
          );
          throw error;
        }
      } else {
        console.log('Firebase App Check: Web platform detected.');
        // Web initialization logic would go here if using the JS SDK directly
        // Currently skipping to avoid unnecessary overhead in dev if not configured
      }
    })();

    return this.initPromise;
  }

  /**
   * Gets the App Check token, ensuring initialization has completed first.
   */
  async getToken(): Promise<{ token: string }> {
    if (this.isSuspended) {
      // Return a rejected promise instead of calling the plugin to avoid rate limits
      return Promise.reject(new Error('App Check suspended due to persistent errors (403/Throttling).'));
    }

    // Ensure initialization is called and awaited
    if (!this.initPromise) {
      await this.initialize();
    } else {
      await this.initPromise;
    }

    try {
      return await FirebaseAppCheck.getToken();
    } catch (error: any) {
      const errorMsg = error?.message || '';

      // If we get an attestation failure (403) or rate limit error, suspend further calls
      // to prevent "Too many attempts" errors originating from the SDK.
      if (errorMsg.includes('403') || errorMsg.includes('Too many attempts')) {
        this.isSuspended = true;
        console.warn(
          'Firebase App Check: Persistent failure detected (403 or Throttling). ' +
          'Suspending App Check for the remainder of this session to avoid rate limits. ' +
          'IMPORTANT: If you see 403, ensure your Debug Token is registered in the Firebase Console.',
        );
      }

      console.error('Error getting Firebase App Check token:', error);
      throw error;
    }
  }

  /**
   * Returns true if App Check is currently suspended due to previous failures.
   */
  isAppCheckSuspended(): boolean {
    return this.isSuspended;
  }
}
